import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import * as ContextMenu from '@radix-ui/react-context-menu';
import { GripVertical, Pencil, EyeOff, Trash2 } from 'lucide-react';
import type { Task, CustomField } from '@/lib/schemas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { KanbanCard } from './kanban-card';
import { useI18n } from '@/lib/i18n';
import type { RenderableColumn } from './kanban-status-config';
import { getColumnDisplayLabel } from './kanban-i18n';

// ============================================
// KANBAN COLUMN
// ============================================
//
// A droppable container that also participates in column-level reordering
// (issue #2 from the original t4 round). The header's grip handle is the
// only part wired to the column-level sortable; the card area stays a
// card drop target so dragging a card onto the body still works.
//
// t8 changes:
//   - Right-click on the column header opens a Radix context menu with
//     Edit / Hide / Delete actions (issue #7). The menu is wired here so
//     the trigger area is co-located with the header, but the actual
//     mutations live in the parent kanban-view which owns the API calls.
//   - On `isOver` we only outline the drop zone — we DO NOT change the
//     background colour anymore. Combined with the kanban-card opacity:0
//     on drag and the opaque DragOverlay card, this keeps the moving
//     card from ever looking translucent over a coloured drop preview
//     (issue #8b).

interface KanbanColumnProps {
    column: RenderableColumn;
    tasks: Task[];
    customFields: CustomField[];
    visibleCardFieldIds: string[];
    onCardClick?: (task: Task) => void;
    /** t15: forwarded from KanbanView → KanbanCard for the subtask
        popover's "open task-detail panel for this subtask" callback. */
    onSelectSubtask?: (task: Task) => void;
    onDeleteColumn?: (columnId: string) => void;
    onEditColumn?: (columnId: string) => void;
    onHideColumn?: (columnId: string) => void;
    dragDisabled?: boolean;
    isOtherColumn?: boolean;
}

// CSS.Translate.toString equivalent (same rationale as in kanban-card.tsx).
function dragTransformToString(transform: { x: number; y: number; scaleX: number; scaleY: number } | null) {
    if (!transform) return undefined;
    const { x, y, scaleX, scaleY } = transform;
    const scale = scaleX !== 1 || scaleY !== 1 ? ` scale(${scaleX}, ${scaleY})` : '';
    return `translate3d(${x}px, ${y}px, 0)${scale}`;
}

// Column header body — shared between the ContextMenu trigger wrapping
// (the clickable, right-clickable surface) and the visual layout. Keeping
// it as a small named component makes the ContextMenu wiring easier to
// read.
function ColumnHeader({
    column,
    displayLabel,
    taskCount,
    isOtherColumn,
    dragDisabled,
    sortableAttributes,
    sortableListeners,
    showDeleteButton,
    onDeleteColumn,
}: {
    column: RenderableColumn;
    displayLabel: string;
    taskCount: number;
    isOtherColumn: boolean;
    dragDisabled: boolean;
    sortableAttributes: Record<string, unknown>;
    sortableListeners: Record<string, unknown> | undefined;
    showDeleteButton: boolean;
    onDeleteColumn?: (columnId: string) => void;
}) {
    return (
        <header className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
            <div className="flex items-center gap-2 min-w-0">
                {!isOtherColumn && !dragDisabled && (
                    <button
                        type="button"
                        aria-label="Drag column"
                        className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-white/10 touch-none"
                        {...(sortableAttributes as any)}
                        {...(sortableListeners as any)}
                    >
                        <GripVertical className="h-3.5 w-3.5 text-foreground-muted" />
                    </button>
                )}
                <Badge
                    variant={column.variant === 'custom' ? 'secondary' : column.variant}
                    className="text-xs"
                >
                    {/* displayLabel is pre-resolved by the parent so we
                        don't call the hook in this tiny sub-component. */}
                    {displayLabel}
                </Badge>
                <span className="text-xs text-foreground-muted">{taskCount}</span>
            </div>
            {!isOtherColumn && showDeleteButton && onDeleteColumn && (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-60 hover:opacity-100"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDeleteColumn(column.id);
                    }}
                    title="Delete column"
                    aria-label="Delete column"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            )}
        </header>
    );
}

export function KanbanColumn({
    column,
    tasks,
    customFields,
    visibleCardFieldIds,
    onCardClick,
    onSelectSubtask,
    onDeleteColumn,
    onEditColumn,
    onHideColumn,
    dragDisabled,
    isOtherColumn,
}: KanbanColumnProps) {
    const { t } = useI18n();

    // Card drop target: a card dragged onto the body of the column
    // resolves here. The column id is `column-${id}` so the board can
    // tell column drops apart from card drags.
    const { isOver, setNodeRef } = useDroppable({
        id: `column-${column.id}`,
        data: { type: 'column', columnId: column.id },
        disabled: isOtherColumn,
    });

    // Sortable wiring for column-level reorders. The drag handle on the
    // header is the only element that triggers a column drag; cards and
    // the column body are ignored so they don't compete with the card
    // drag handler.
    const {
        attributes,
        listeners,
        setNodeRef: setSortableRef,
        transform,
        isDragging,
    } = useSortable({
        id: column.id,
        data: { type: 'column-handle', columnId: column.id },
        disabled: dragDisabled || isOtherColumn,
    });

    const style: React.CSSProperties = {
        transform: dragTransformToString(transform),
        // t15 flicker fix: dnd-kit's <SortableContext> injects a global
        // `[data-dnd-kit-sortable] { transition: transform 200ms ease; }`
        // rule. Inline `transition: 'none'` overrides that rule at all
        // times (both during drag and after drop). The previous use of
        // `transition: undefined` post-drop let the dnd-kit rule apply,
        // which animated each item from its dragged offset back to
        // identity over 200ms — to the user that looks like the column
        // "snap back to the OLD position then settle on NEW".
        // With `transition: 'none'` the drop is instant on both ends.
        transition: 'none',
    };

    // When the user is column-dragging, keep the lane visible at low
    // opacity so the destination preview is obvious.
    const showAsDragging = isDragging && !isOtherColumn;

    const showContextMenu = !isOtherColumn && !dragDisabled;

    // The header body is reused inside the ContextMenu trigger wrapper.
    // Right-click anywhere on the header opens the menu; left-click on
    // the GripVertical still triggers the column-reorder drag (the menu
    // ignores left-click).
    // t12: resolve the localised column header ONCE here so the
    // sub-component doesn't need its own hook.
    const displayLabel = getColumnDisplayLabel(column, t);
    const headerEl = (
        <ColumnHeader
            column={column}
            displayLabel={displayLabel}
            taskCount={tasks.length}
            isOtherColumn={!!isOtherColumn}
            dragDisabled={!!dragDisabled}
            sortableAttributes={attributes as unknown as Record<string, unknown>}
            sortableListeners={listeners as unknown as Record<string, unknown> | undefined}
            showDeleteButton={!!onDeleteColumn}
            onDeleteColumn={onDeleteColumn}
        />
    );

    return (
        <div
            ref={setSortableRef}
            style={style}
            className={cn(
                // t6: columns distribute available width on wide viewports
                // and still scroll horizontally when there are too many to
                // fit. min-w-72 keeps narrow screens usable; flex-1 lets a
                // typical 5-column board fill a 1920px viewport edge-to-edge.
                // R6 responsive: on mobile (≤640px) the parent
                // columns container switches to `flex-col`, so we drop
                // basis/flex sizing here and let each column take full
                // width. `basis-full sm:basis-72` keeps desktop layout.
                'flex flex-col basis-full sm:basis-72 sm:flex-1 min-w-0 sm:min-w-72 bg-white/5 border border-white/10 rounded-xl',
                showAsDragging && 'opacity-50'
            )}
            data-testid={`kanban-column-${column.id}`}
        >
            {showContextMenu ? (
                <ContextMenu.Root>
                    <ContextMenu.Trigger asChild>
                        <div className="cursor-context-menu">{headerEl}</div>
                    </ContextMenu.Trigger>
                    <ContextMenu.Portal>
                        <ContextMenu.Content
                            className="min-w-[180px] rounded-md border border-border bg-surface p-1 shadow-lg z-50"
                        >
                            {onEditColumn && (
                                <ContextMenu.Item
                                    className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-surface-hover cursor-pointer outline-none"
                                    onSelect={() => onEditColumn(column.id)}
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                    Edit column…
                                </ContextMenu.Item>
                            )}
                            {onHideColumn && (
                                <ContextMenu.Item
                                    className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-surface-hover cursor-pointer outline-none"
                                    onSelect={() => onHideColumn(column.id)}
                                >
                                    <EyeOff className="h-3.5 w-3.5" />
                                    Hide column
                                </ContextMenu.Item>
                            )}
                            {onDeleteColumn && (
                                <ContextMenu.Item
                                    className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-destructive/20 text-destructive cursor-pointer outline-none"
                                    onSelect={() => onDeleteColumn(column.id)}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    Delete column
                                </ContextMenu.Item>
                            )}
                        </ContextMenu.Content>
                    </ContextMenu.Portal>
                </ContextMenu.Root>
            ) : (
                headerEl
            )}

            <div
                ref={setNodeRef}
                className={cn(
                    'flex-1 min-h-[120px] p-2 space-y-1.5 overflow-y-auto max-h-[calc(100vh-280px)] transition-colors',
                    // t8 fix: outline only, no background tint. Combined
                    // with the kanban-card opacity:0 on drag and the opaque
                    // DragOverlay copy, the moving card stays visually
                    // crisp instead of competing with a coloured drop
                    // preview for visibility.
                    isOver && 'outline outline-2 outline-primary/60 outline-offset-[-2px]'
                )}
            >
                {tasks.length === 0 ? (
                    <div className="flex items-center justify-center h-24 rounded-lg border border-dashed border-white/10 text-[11px] text-foreground-muted">
                        Drop a card here
                    </div>
                ) : (
                    tasks.map((task) => (
                        <KanbanCard
                            key={task.id}
                            task={task}
                            customFields={customFields}
                            visibleCardFieldIds={visibleCardFieldIds}
                            onClick={onCardClick}
                            onSelectSubtask={onSelectSubtask}
                            disabled={dragDisabled}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

// Re-export the CSS helper for callers that want to compose transforms.
export { dragTransformToString as columnDragTransformToString };
